import { computed, type ComputedRef, type Ref, watch } from 'vue'

import type { LegalConsentsValues } from '@/components/ai/onboarding/OnboardingLegalConsents.vue'

import {
  readOnboardingLegalConsents,
  writeOnboardingLegalConsents,
} from '../lib/onboardingProgressStorage'
import type { MissingOnboardingArea } from './useOnboardingDataCompleteness'

type Translate = (key: string, ...args: unknown[]) => string
type MaybeRef<T> = Ref<T> | ComputedRef<T>

/**
 * Required-consent rules and reload persistence for the onboarding legal
 * consents, shared by the form's last step and the review page.
 *
 * US accounts accept the privacy policy with the signup consents, so they get
 * the required send-on-behalf authorization instead of `acceptPrivacy`.
 */
export function useOnboardingLegalConsents(options: {
  companyId: MaybeRef<string | null | undefined>
  consents: LegalConsentsValues
  requiresAgb: MaybeRef<boolean>
  requiresGuideline: MaybeRef<boolean>
  isUsOnboarding?: MaybeRef<boolean>
  legalCompanyName: MaybeRef<string | null | undefined>
  readablePlatformName: MaybeRef<string | null | undefined>
  t: Translate
}) {
  const { companyId, consents, requiresAgb, requiresGuideline, isUsOnboarding, t } = options

  const requiredConsents = computed<Array<[keyof LegalConsentsValues, boolean, string]>>(() => {
    const isUs = Boolean(isUsOnboarding?.value)
    const company = options.legalCompanyName.value
    return [
      ['acceptAgb', requiresAgb.value, t('onboarding.submit.termsOf', { company })],
      ['acceptPrivacy', !isUs, t('onboarding.submit.privacyPolicyOf', { company })],
      [
        'acceptGuideline',
        requiresGuideline.value,
        t('onboarding.submit.guidelineFor', { platform: options.readablePlatformName.value }),
      ],
      ['authorizeSendOnBehalf', isUs, t('onboarding.wizard.data-entry.consent-send-on-behalf')],
      ['acceptConfirmData', true, t('onboarding.wizard.data-entry.consent-confirm-data')],
    ]
  })

  const missingConsentFields = computed(() =>
    requiredConsents.value
      .filter(([key, isRequired]) => isRequired && !consents[key])
      .map(([key, , label]) => ({ key, label }))
  )

  const reviewAcceptancesValid = computed(() => missingConsentFields.value.length === 0)

  /** Unticked required consents as a summary area, for the form's "Still missing" alert. */
  const missingLegalConsents = computed<MissingOnboardingArea[]>(() =>
    missingConsentFields.value.length
      ? [
          {
            areaLabel: t('onboarding.wizard.review.acceptances-intro'),
            fields: missingConsentFields.value,
          },
        ]
      : []
  )

  // Consents are only sent on the final submit: keep ticked boxes across a
  // reload. Restored before the caller applies chat answers, so a confirmed
  // chat submission still wins over the stored values.
  let restoredFor: string | null = null
  watch(
    companyId,
    (id) => {
      if (!id || restoredFor === id) return
      restoredFor = id
      const stored = readOnboardingLegalConsents(id)
      if (!stored) return
      for (const key of Object.keys(consents) as Array<keyof LegalConsentsValues>) {
        if (typeof stored[key] === 'boolean') consents[key] = stored[key]
      }
    },
    { immediate: true }
  )
  watch(
    consents,
    (values) => {
      const id = companyId.value
      // Don't overwrite the stored values before they were restored for this company.
      if (!id || restoredFor !== id) return
      writeOnboardingLegalConsents(id, { ...values })
    },
    { deep: true }
  )

  return { reviewAcceptancesValid, missingLegalConsents }
}
